import React from "react";
import { Space } from "antd";
import Table from "../../commonComponents/Table";
import TablePagination from "../../commonComponents/Pagination";
import { PromotionsTableProps, Promotion } from "./types";
import { ColumnsType } from "antd/es/table";
import { message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPen, faEye, faCopy } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../context/ThemeContext";
import {
  ActionIconButton,
  PromoCodeWrapper,
  PromoCodeText,
  CopyCodeButton,
  TitleWrapper,
  TableTitleText,
  TableText,
  DiscountText,
} from "./Styles";

const PromotionsTable: React.FC<PromotionsTableProps> = ({
  promotions,
  currentPage,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  onViewDetails,
  onEdit,
  onDelete,
  onCopyCode,
  onCreate,
  getStatusBadge,
  getTypeLabel,
  loading = false,
}) => {
  const { isDarkMode } = useTheme();
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    message.success(`Code "${code}" copied to clipboard.`);
    onCopyCode(code);
  };

  const columns: ColumnsType<Promotion> = [
    {
      title: "TITLE",
      key: "title",
      fixed: "left",
      width: 200,
      render: (_, record) => (
        <TitleWrapper>
          <TableTitleText>{record.title}</TableTitleText>
        </TitleWrapper>
      ),
    },
    {
      title: "PROMO CODE",
      key: "code",
      width: 180,
      render: (_, record) => {
        const code = record.promoCode || record.code || "N/A";
        return (
          <PromoCodeWrapper>
            <PromoCodeText>{code}</PromoCodeText>
            <CopyCodeButton onClick={() => handleCopyCode(code)}>
              <FontAwesomeIcon icon={faCopy} fontSize={14} color="#8c8c8c" />
            </CopyCodeButton>
          </PromoCodeWrapper>
        );
      },
    },
    {
      title: "TYPE",
      key: "type",
      width: 150,
      render: (_, record) => (
        <TableText>{getTypeLabel(record.type)}</TableText>
      ),
    },
    {
      title: "DISCOUNT",
      key: "discount",
      width: 120,
      render: (_, record) => {
        let discountText = "BOGO";
        if (record.type === "percentage") {
          discountText = `${record.discountValue}%`;
        } else if (record.type === "fixed") {
          discountText = `$${record.discountValue}`;
        }
        return (
          <DiscountText>{discountText}</DiscountText>
        );
      },
    },
    {
      title: "USAGE",
      key: "usage",
      width: 120,
      render: (_, record) => (
        <TableText>
          {record.usageCount || 0} / {record.usageLimit || "∞"}
        </TableText>
      ),
    },
    {
      title: "STATUS",
      key: "status",
      width: 120,
      render: (_, record) => getStatusBadge(record),
    },
    {
      title: "VALID UNTIL",
      key: "validUntil",
      width: 150,
      render: (_, record) => (
        <TableText>
          {record.endDate
            ? new Date(record.endDate).toLocaleDateString()
            : "N/A"}
        </TableText>
      ),
    },
    {
      title: "ACTIONS",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <ActionIconButton
            onClick={() => onViewDetails(record)}
            iconColor="#2D6CCA"
          >
            <FontAwesomeIcon icon={faEye} fontSize={14} color={isDarkMode ? "#fff" : "#222222"} />
          </ActionIconButton>
          <ActionIconButton onClick={() => onEdit(record)} iconColor="#2D6CCA">
            <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? "#fff" : "#222222"} />
          </ActionIconButton>
          <ActionIconButton
            onClick={() => onDelete(record)}
            iconColor="#f5222d"
          >
            <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? "#fff" : "#222222"} />
          </ActionIconButton>
        </Space>
      ),
    },
  ];

  const handlePaginationChange = (page: number, size: number) => {
    if (size && size !== pageSize) {
      onPageSizeChange(size);
    }
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  return (
    <>
      <Table<Promotion>
        columns={columns}
        dataSource={promotions}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: true, y: 600 }}
        emptyText="No promotions found"
        headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
        removeVerticalBorders={true}
        thPadding="15px 10px"
        tdPadding="10px 10px"
        firstChildBorderRadius="8px"
        border="0"
        borderBottomLeftRight={true}
        theme={isDarkMode ? "dark" : "light"}
      />
      {promotions.length > 0 && (
        <TablePagination
          totalCount={total}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
          totalLabel="Total records"
          padding="16px 12px 0px 12px"
        />
      )}
    </>
  );
};

export default PromotionsTable;
